import {
  BottomSheet,
  Input,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics/log";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useUpdateWorker } from "@src/appV2/Worker/useUpdateWorker";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { DialogFooter } from "../../components/DialogFooter";
import { LoadingButton } from "../../components/LoadingButton";
import { TextButton } from "../../components/TextButton";
import { useAddSsn } from "../hooks/useAddSsn";

interface AddSsnBottomSheetProps {
  readonly modalState: UseModalState;
  readonly onSsnAdded: () => void;
}

export function AddSsnBottomSheet(props: AddSsnBottomSheetProps) {
  const { modalState, onSsnAdded } = props;
  const { openArticle } = useZendeskArticle();
  const { whySsnSupportLink } = useSupportLinks();
  const { showErrorToast } = useToast();

  const worker = useDefinedWorker();

  const { mutateAsync: updateWorkerSsn, isLoading: isUpdatingWorkerSsn } = useUpdateWorker();

  const { ssn, handleChangeSSN, handleKeyUpSSN, handleKeyPressSSN, handleSaveSSN, isDisabled } =
    useAddSsn();

  async function onSave(ssnNumber: string) {
    try {
      await updateWorkerSsn({
        fullSocialSecurityNumber: ssnNumber,
      });

      onSsnAdded();

      modalState.closeModal();
    } catch (error) {
      logError(APP_V2_USER_EVENTS.UPDATE_WORKER_SSN_FAILURE, {
        error,
        metadata: {
          workerId: worker.userId,
        },
      });
      showErrorToast("Unexpected error occurred while saving ssn");
    }
  }

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            isLoading={isUpdatingWorkerSsn}
            disabled={isDisabled}
            onClick={async () => {
              await handleSaveSSN(onSave);
            }}
          >
            Continue
          </LoadingButton>
          <TextButton
            size="large"
            sx={{ marginTop: 1, fontWeight: "bold" }}
            onClick={async () => {
              await openArticle(whySsnSupportLink);
            }}
          >
            Why Clipboard asks for your SSN
          </TextButton>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={7}>
          <Title component="h2" textAlign="center">
            We need your SSN to verify this license
          </Title>
          <Text>
            We share your Social Security Number only with government and background check agencies
            to ensure compliance with state and federal laws. We securely store an encrypted version
            of your SSN.
          </Text>

          <Stack spacing={3}>
            <Text semibold variant="body2" pl={1}>
              Social Security Number
            </Text>
            <Input
              fullWidth
              size="large"
              value={ssn}
              onChange={handleChangeSSN}
              onKeyUp={handleKeyUpSSN}
              onKeyPress={handleKeyPressSSN}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
